import {getTestProps} from '@benchling/js-test-utils';
import {Chip, createStyles, Text} from '@mantine/core';
import React from 'react';

import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {HOMEPAGE_SECTION_TEST_COMPONENT} from '../HomepageConstants';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.MY_WORK;

const useStatusTabsListStyles = createStyles((theme) => ({
  tabsList: {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 10,
  },
}));

const useStatusTabStyles = createStyles((theme, {color}: {color: string}) => ({
  tab: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '100%',
    minWidth: 0,
    height: 75,
    backgroundColor: theme.colors[color][1],
    borderRadius: theme.radius.md,
    outline: '1px solid transparent',
    outlineOffset: -1,
    cursor: 'pointer',
    '&[data-active]': {
      outlineColor: theme.colors[color][6],
    },
    '& .mantine-Chip-input': {
      position: 'absolute',
      opacity: 0,
    },
    '& .mantine-Chip-iconWrapper': {
      display: 'none',
    },
    '& .mantine-Chip-label': {
      width: '100%',
      height: '100%',
      marginBottom: 0,
      cursor: 'pointer',
    },
    '& .mantine-Chip-input:focus-visible ~ .mantine-Chip-label': {
      boxShadow: `0 0 0 2px ${theme.colors.blue[4]}`,
    },
  },
  statusSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    lineHeight: 1,
  },
  tabContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    width: '100%',
    height: '100%',
  },
  statusLabel: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '16px',
  },
  icon: {
    color: theme.fn.themeColor(color, 6),
    '&&': {
      width: '20px',
      height: '20px',
    },
  },
  countText: {
    fontWeight: theme.other.fontWeights.semibold,
    fontSize: 20,
    lineHeight: '20px',
  },
}));

export interface MyWorkStatusTabData<StatusLabel extends string> {
  color: string;
  count: number;
  icon: BenchlingIconName;
  label: StatusLabel;
}

interface MyWorkStatusTabsProps<StatusLabel extends string> {
  ariaLabel: string;
  selectedStatus: StatusLabel;
  statuses: Array<MyWorkStatusTabData<StatusLabel>>;
  onStatusChange: (status: StatusLabel) => void;
}

function MyWorkStatusChip<StatusLabel extends string>({
  isSelected,
  status,
}: {
  isSelected: boolean;
  status: MyWorkStatusTabData<StatusLabel>;
}): JSX.Element {
  const {classes} = useStatusTabStyles({color: status.color});

  return (
    <Chip
      key={status.label}
      value={status.label}
      type="radio"
      unstyled={true}
      classNames={{
        root: classes.tab,
      }}
      wrapperProps={{
        'data-active': isSelected ? true : undefined,
        ...getTestProps({component: TEST_COMPONENT, element: 'statusTab', key: status.label}),
      }}
    >
      <div className={classes.tabContent}>
        <div className={classes.statusSummary}>
          <BenchlingIcon icon={status.icon} className={classes.icon} aria-hidden={true} />
          <Text
            span={true}
            className={classes.countText}
            {...getTestProps({component: TEST_COMPONENT, element: 'statusTabCount', key: status.label})}
          >
            {status.count}
          </Text>
        </div>
        <div className={classes.statusLabel}>{status.label}</div>
      </div>
    </Chip>
  );
}

export default function MyWorkStatusTabs<StatusLabel extends string>({
  ariaLabel,
  selectedStatus,
  statuses,
  onStatusChange,
}: MyWorkStatusTabsProps<StatusLabel>): JSX.Element {
  const {classes} = useStatusTabsListStyles();

  return (
    <Chip.Group
      multiple={false}
      value={selectedStatus}
      onChange={(status) => {
        onStatusChange(status as StatusLabel);
      }}
    >
      <div role="radiogroup" aria-label={ariaLabel} className={classes.tabsList}>
        {statuses.map((status) => {
          const isSelected = status.label === selectedStatus;

          return <MyWorkStatusChip key={status.label} status={status} isSelected={isSelected} />;
        })}
      </div>
    </Chip.Group>
  );
}
